/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable no-nested-ternary */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainBody from "../../../components/MainBody/MainBody";

import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Banknote,
  // Eye,
  FileSearch,
  Globe,
  Percent,
  ShieldCheck,
  User,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "../../../components/ui/card";
// import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";
import { API, get } from "../../../config";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import moment from "moment";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../../../components/ui/chart";
import { formatNumber } from "../../../lib/Misc";
import TokenHistory from "../../Subscription/TokenHistory";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import CommonCDD from "../CDD/CommonCDD";
import { useUser } from "../../../lib/context/UserContext";

// const RISK_COLORS = ["#52c41a", "#faad14", "#ff4d4f"];
const COLORS = ["#52c41a", "#faad14", "#ff4d4f"];
// const COLORS = [
//   "#1F78B4",
//   "#1B9BDF",
//   "#034262",
//   "#C7DAE3",
//   "#A6CEE3",
//   "#B2DF8A",
//   "#d3f7b2",
//   "#2b6a8a",
//   "#6a9aba",
//   "#0f71a3",
// ];

const STAT_CARD_CONFIG = [
  {
    key: "pending_kyc",
    title: "Individuals Awaiting Screening",
    subtitle: "Pending Screening",
    icon: FileSearch,
    db_key: "pending",
  },
  {
    key: "verified_kyc",
    title: "Individuals Checked",
    subtitle: "KYC Completed",
    icon: User,
    db_key: "verified",
  },
  {
    key: "verified_kyb",
    title: "Businesses Checked",
    subtitle: "KYB Completed",
    icon: Banknote,
    db_key: "kyb_verified",
  },
  {
    key: "verified_percentage",
    title: "% of Contact's Checked",
    subtitle: "Overall Screening Rate",
    icon: Percent,
    db_key: "total_verified",
    isPercentage: true,
  },
  {
    key: "pep_total",
    title: "Politically Exposed Person Matches",
    subtitle: "PEP Matches Identified",
    icon: ShieldCheck,
    db_key: "pep",
  },
  {
    key: "sanction_total",
    title: "Sanctions List Matches",
    subtitle: "Sanctions Matches Identified",
    icon: AlertTriangle,
    db_key: "sanction",
  },
  {
    key: "adverse_media_total",
    title: "Adverse Media",
    subtitle: "Adverse Media Matches",
    icon: Globe,
    db_key: "adverse_media_result",
  },
  {
    key: "designated_service_customers",
    title: "Contact Getting Designated Service",
    subtitle: "Designated Services",
    icon: AlertTriangle,
    db_key: "designated_service_customers",
  },
];

const getCreditPercentage = (credit) => {
  const total = Number(credit?.total || 0);
  const used = Number(credit?.used || 0);

  if (!total) {
    return 0;
  }

  return Number(Math.min((used / total) * 100, 100).toFixed(2));
};

const ContactHubDashboard = ({
  tabMode,
  amlDashboardValue = "all",
  selectedMonthRange,
}) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTokenHistory, setShowTokenHistory] = useState(false);
  const navigate = useNavigate();
  const { organizationData } = useOrganization();
  const [showDetails, setShowDetails] = useState(false);
  const [detailType, setDetailType] = useState(false);

  const { permission } = useUser();

  const toggleDashboardDetail = (type) => {
    if (type) {
      setDetailType(type);
    }
    setShowDetails(!showDetails);
  };

  /**
   * Fetch dashboard data.
   */
  const fetchContactDashboard = useCallback(
    async (startDate, endDate) => {
      try {
        setLoading(true);

        const params = new URLSearchParams({
          start_date: startDate || "",
          end_date: endDate || "",
          organization_id: organizationData?.organizationData?.id
            ? organizationData?.organizationData?.id
            : "",
          section: amlDashboardValue,
        });

        const { data } = await get(
          `${API.GET_AML_DASHBOARD}?${params.toString()}`,
        );

        setDashboardData(data?.data || null);
      } catch (error) {
        setDashboardData(null);

        const errorMessage = error?.response?.data?.errors?.myna_error?.[0];

        if (errorMessage) {
          if (error?.response?.data?.responseType === "warning") {
            toast.warn(errorMessage);
          } else {
            toast.error(errorMessage);
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [organizationData?.organizationData?.id, amlDashboardValue],
  );

  /**
   * Fetch dashboard when filters/tab change.
   */
  useEffect(() => {
    fetchContactDashboard(
      moment(selectedMonthRange?.start).format("MM-YYYY"),
      moment(selectedMonthRange?.end).format("MM-YYYY"),
    );
  }, [
    selectedMonthRange?.start,
    selectedMonthRange?.end,
    fetchContactDashboard,
  ]);

  /**
   * Keep existing tab navigation behavior.
   */
  useEffect(() => {
    navigate(
      `?tab=dashboard&childTab=contact-hub-dashboard&from=${
        moment(selectedMonthRange?.start).format("MM-YYYY")
          ? moment(selectedMonthRange?.start).format("MM-YYYY")
          : ""
      }&to=${
        moment(selectedMonthRange?.end).format("MM-YYYY")
          ? moment(selectedMonthRange?.end).format("MM-YYYY")
          : ""
      }`,
      { replace: true },
    );
  }, [tabMode, navigate]);

  /**
   * Screening activity chart data.
   */
  const customerData = useMemo(
    () =>
      dashboardData?.customer_screeing_trend?.map((item) => ({
        month: item.month,
        Individuals: item.individuals,
        "Non Individuals": item.non_individuals,
      })) || [],
    [dashboardData?.customer_screeing_trend],
  );

  /**
   * Risk breakdown chart data.
   */
  const riskData = useMemo(
    () =>
      Object.entries(dashboardData?.customer_risks || {}).map(
        ([name, value]) => ({
          name,
          value,
        }),
      ),
    [dashboardData?.customer_risks],
  );

  /**
   * Statistic cards.
   */
  const statCards = useMemo(
    () =>
      STAT_CARD_CONFIG.map((item) => ({
        ...item,
        value: item.isPercentage
          ? `${dashboardData?.[item.key] ?? 0}%`
          : (dashboardData?.[item.key] ?? 0),
      })),
    [dashboardData],
  );

  const kycCredits = dashboardData?.kyc_details?.kyc || {};
  const kybCredits = dashboardData?.kyc_details?.kyb || {};

  const kycPercentage = useMemo(
    () => getCreditPercentage(kycCredits),
    [kycCredits],
  );

  const kybPercentage = useMemo(
    () => getCreditPercentage(kybCredits),
    [kybCredits],
  );

  const toggleTokenHistory = useCallback(() => {
    setShowTokenHistory((previous) => !previous);
  }, []);

  const hasTokenHistory = Boolean(
    dashboardData?.kyc_details?.cdd_usage_details,
  );

  const barChartConfig = {
    individuals: {
      label: "Individual",
      color: "hsl(var(--chart-3))",
    },
    non_individuals: {
      label: "Non Individual",
      color: "hsl(var(--chart-4))",
    },
  };

  const navigateBar = (data, type) => {
    const input = data?.payload?.month;
    if (input) {
      // Parse the date
      const parsedDate = new Date(input);

      // Get year and month
      const year = parsedDate.getFullYear();
      const month = parsedDate.getMonth(); // 0-based (June is 5)

      // First day of the month
      const fromDate = new Date(year, month, 1);

      // Last day of the month
      const toDate = new Date(year, month + 1, 0);

      // Format as dd-mm-yyyy
      const formatDate = (date) =>
        `${String(date.getDate()).padStart(2, "0")}-${String(
          date.getMonth() + 1,
        ).padStart(2, "0")}-${date.getFullYear()}`;

      const from = formatDate(fromDate);
      const to = formatDate(toDate);

      console.log(from, to);

      // let url =
      //   type === "reconciled"
      //     ? `/trusts/${trustId}/banking?tab=reconcile-item&limit=${defaultLimitPagination}&page=1&from=${from}&to=${to}`
      //     : type === "unreconciled"
      //     ? `/trusts/${trustId}/banking?tab=reconcile-transactions&view=grid&from=${from}&to=${to}`
      //     : type === "receipts"
      //     ? `/trusts/${trustId}/receipts?limit=${defaultLimitPagination}&page=1&from=${from}&to=${to}`
      //     : type === "payments"
      //     ? `/trusts/${trustId}/payments?limit=${defaultLimitPagination}&page=1&from=${from}&to=${to}`
      //     : `/trusts/${trustId}/dashboard`;

      let url = "";
      navigate(url);
    }
  };

  return (
    <MainBody
      loading={loading}
      search={false}
      innerBody={true}
      parentHeight={true}
    >
      <div className="space-y-4">
        {/* -----------------------------------------------------------
          KPI CARDS
      ------------------------------------------------------------ */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {statCards.map((item) => {
            const Icon = item.icon;

            return (
              <Card
                key={item.key}
                className=" relative group hover:cursor-pointer hover:bg-muted dark:hover:text-white min-w-0 hover:shadow-md transition-all duration-200 ease-in-out hover:-translate-y-1 hover:border-webPrimary active:translate-y-0"
                onClick={() => toggleDashboardDetail(item?.db_key)}
              >
                <CardContent className="p-5 ">
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-5 dark:group-hover:text-white text-muted-foreground">
                          {item.title}
                        </p>

                        <p className="mt-3 text-2xl font-bold tracking-tight">
                          {item.value}
                        </p>
                      </div>

                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted group-hover:bg-webPrimary">
                        <Icon className="h-5 w-5 text-muted-foreground group-hover:text-white" />
                      </div>
                    </div>
                  </>
                </CardContent>
                <p className="absolute bottom-0 rounded-ss-lg rounded-ee-lg right-0  mt-1 text-xs text-white bg-webPrimary  w-fit p-1 px-2">
                  {item.subtitle}
                </p>
              </Card>
            );
          })}
        </div>

        {/* -----------------------------------------------------------
          CREDIT USAGE
      ------------------------------------------------------------ */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <CreditUsageCard
            title="Individual Screening Credits"
            credit={kycCredits}
            percentage={kycPercentage}
            showHistory={hasTokenHistory}
            type={1}
            onHistoryClick={toggleTokenHistory}
          />

          <CreditUsageCard
            title="Business Screening Credits"
            credit={kybCredits}
            percentage={kybPercentage}
            showHistory={hasTokenHistory}
            type={2}
            onHistoryClick={toggleTokenHistory}
          />
        </div>

        {/* -----------------------------------------------------------
          CHARTS
      ------------------------------------------------------------ */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Screening Activity */}
          <Card className="group transition-all hover:bg-accent hover:cursor-pointer">
            <CardHeader className="p-4 pb-6 text-center items-center">
              <h3 className="relative px-3 py-0.5 bg-muted rounded-md shadow-sm text-base font-semibold w-fit">
                Screening Activity Over Time
              </h3>
              <CardDescription>
                {moment(selectedMonthRange?.start)?.format("MMM-YYYY") ===
                moment(selectedMonthRange?.end)?.format("MMM-YYYY")
                  ? moment(selectedMonthRange?.start)?.format("MMM-YYYY")
                  : `${moment(selectedMonthRange?.start)?.format(
                      "MMM-YYYY",
                    )} ~ ${moment(selectedMonthRange?.end)?.format(
                      "MMM-YYYY",
                    )}`}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <ChartContainer config={barChartConfig}>
                <BarChart accessibilityLayer data={customerData}>
                  <CartesianGrid
                    vertical={false}
                    className="!stroke-border group-hover:dark:!stroke-muted-foreground"
                  />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                    tickFormatter={(value) => value.slice(0, 3)}
                  />
                  <YAxis />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        hideLabel
                        className="w-[180px]"
                        formatter={(value, name, item, index) => (
                          <>
                            {index === 0 && (
                              <div className="mb-1 flex basis-full items-center text-xs font-bold text-foreground">
                                {item.payload.month}
                              </div>
                            )}
                            <div
                              className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                              style={{
                                "--color-bg": `var(--color-${name === "Individuals" ? "individuals" : "non_individuals"})`,
                              }}
                            />
                            {barChartConfig[name]?.label || name}
                            <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                              {formatNumber(Number(value))}
                            </div>
                          </>
                        )}
                      />
                    }
                    cursor={false}
                  />
                  <Bar
                    dataKey="Individuals"
                    fill="var(--color-individuals)"
                    radius={[4, 4, 0, 0]}
                    className="hover:cursor-pointer"
                    onClick={(data, index) => {
                      navigateBar(data, "individuals");
                    }}
                  />
                  <Bar
                    dataKey="Non Individuals"
                    fill="var(--color-non_individuals)"
                    radius={[4, 4, 0, 0]}
                    className="hover:cursor-pointer"
                    onClick={(data, index) => {
                      navigateBar(data, "non_individuals");
                    }}
                  />
                  <ChartLegend
                    content={<ChartLegendContent />}
                    className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
                  />
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Risk Breakdown */}
          <Card className="group transition-all hover:bg-accent hover:cursor-pointer">
            <CardHeader className="items-center p-4">
              <h3 className="relative px-3 py-0.5 bg-muted rounded-md shadow-sm text-base font-semibold w-fit">
                Contact Hub Risk Breakdown
              </h3>
              <CardDescription>
                {moment(selectedMonthRange?.start)?.format("MMM-YYYY") ===
                moment(selectedMonthRange?.end)?.format("MMM-YYYY")
                  ? moment(selectedMonthRange?.start)?.format("MMM-YYYY")
                  : `${moment(selectedMonthRange?.start)?.format(
                      "MMM-YYYY",
                    )} ~ ${moment(selectedMonthRange?.end)?.format(
                      "MMM-YYYY",
                    )}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 p-4 pt-0">
              <ChartContainer
                config={riskData}
                className="mx-auto max-h-[400px]"
              >
                <PieChart>
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        nameKey="name"
                        hideLabel
                        formatter={(value, name, item, index) => (
                          <>
                            <div
                              className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[var(--color-bg)]"
                              style={{
                                "--color-bg": `${
                                  COLORS[
                                    riskData?.findIndex(
                                      (item) =>
                                        item.value === value &&
                                        item.name === name,
                                    ) % COLORS.length
                                  ]
                                }`,
                              }}
                            />
                            {riskData[name]?.label || name}
                            <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                              {/* {formatNumber(
                                Number(item?.payload?.payload?.amount),
                              )}{" "} */}
                              {`${value}%`}
                            </div>
                          </>
                        )}
                      />
                    }
                  />
                  <Pie
                    data={riskData}
                    dataKey="value"
                    label={({ payload, ...props }) => {
                      return (
                        <text
                          cx={props.cx}
                          cy={props.cy}
                          x={props.x}
                          y={props.y}
                          textAnchor={props.textAnchor}
                          dominantBaseline={props.dominantBaseline}
                          fill={COLORS[props?.index % COLORS.length]}
                        >
                          {`${payload.value}`}
                        </text>
                      );
                    }}
                  >
                    {riskData?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        onClick={(data) => {
                          navigateBar(entry, "vendorPayment");
                        }}
                      />
                    ))}
                  </Pie>
                  <ChartLegend
                    content={<ChartLegendContent nameKey="name" />}
                    className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center flex-col items-start inline-flex max-w-44"
                    verticalAlign="bottom"
                    align="right"
                    layout="vertical"
                  />
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        {/* -----------------------------------------------------------
          TOKEN HISTORY MODAL
      ------------------------------------------------------------ */}
        <Dialog open={showTokenHistory} onOpenChange={setShowTokenHistory}>
          <DialogContent className="max-h-[90vh] w-[95vw] max-w-6xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Token History</DialogTitle>
            </DialogHeader>

            {/* <div className="space-y-4">
            <div className="flex items-center justify-end">
              <CDDLimtShow
                cddLimit={dashboardData?.kyc_details}
                tab
              />
            </div>

            <div className="customer-table">
              <TokenUsage
                data={
                  dashboardData?.kyc_details?.cdd_usage_details
                }
              />
            </div>
          </div> */}
          </DialogContent>
        </Dialog>

        <ResponsiveDrawerForm
          description={""}
          title={
            STAT_CARD_CONFIG?.find((item) => item.db_key === detailType)?.title
          }
          handleOpen={() => {}}
          handleClose={() => {
            setShowDetails(false);
          }}
          className={"md:max-w-[95vw] overflow-hidden"}
          open={showDetails}
          buttonClassName={""}
          setOpen={setShowDetails}
          read={true}
        >
          <CommonCDD
            type={detailType}
            startDate={moment(selectedMonthRange?.start).format("MM-YYYY")}
            endDate={moment(selectedMonthRange?.end).format("MM-YYYY")}
            readAccess={permission?.["cdds"]?.read}
            writeAccess={permission?.["cdds"]?.write}
          />
        </ResponsiveDrawerForm>
      </div>
    </MainBody>
  );
};

/**
 * ---------------------------------------------------------------
 * Credit Usage Card
 * ---------------------------------------------------------------
 */
const CreditUsageCard = ({ title, credit, percentage, type }) => {
  const total = Number(credit?.total || 0);
  const used = Number(credit?.used || 0);
  const remaining = Math.max(total - used, 0);

  return (
    <Card className="group hover:bg-muted/50 dark:hover:text-white min-w-0 hover:shadow-md transition-all duration-200 ease-in-out hover:-translate-y-1 hover:border-webPrimary active:translate-y-0">
      <CardHeader className="pb-3 pt-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="relative dark:px-3 dark:py-0.5 dark:bg-webPrimary dark:text-white dark:rounded-md dark:shadow-sm text-base font-semibold w-fit">
            {title}
          </h3>

          <TokenHistory defaultType={type} />
        </div>
      </CardHeader>

      <CardContent>
        <Progress value={percentage} className="h-2" />

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm font-medium">{remaining} Credits Remaining</p>

          <span className="text-sm font-semibold text-muted-foreground">
            {percentage}%
          </span>
        </div>

        <p className="mt-1 text-xs text-muted-foreground">
          Used {used} of {total}
        </p>
      </CardContent>
    </Card>
  );
};

export default ContactHubDashboard;
